import { ITEMS_PER_PAGE } from "#constants";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  usePagination,
  useSort,
  type GroupingType,
  type PaginationType,
  type SortingType,
} from "@validereinc/common-components";
import { SortDirection } from "@validereinc/domain";
import { usePrevious } from "@validereinc/domain-controllers";
import { isEmpty, isEqual } from "es-toolkit/compat";
import { produce } from "immer";
import { useEffect, useRef } from "react";

export type TableStateType = PaginationType & SortingType & GroupingType;

/**
 * Determines if the page needs to be reset back to 1 and data be fetched for
 * that page instead. Keeps track of any applied filters and the page # + total
 * results during the time of filter change - then sets a flag to indicate that
 * resetting should occur. when this hook runs again, the filters will not have
 * changed so what we're waiting for instead is for the page # or total # of
 * results to be different - if either of those changed, that means no data has
 * come in and we're safe to turn off the resetting flag.
 *
 * The reason we'd want to do this all is because if you're on any page but 1,
 * and apply filters that would return less # of results than the page you're
 * on, with the way the BE APIs work (CarbonHub in particular) you make a
 * request for that page and get no results back - which is incorrect because
 * the total entries usually indicates that there is data... in the smaller
 * pages. The UI also doesn't help in this case because the pagination controls
 * dissapear... so it looks like there indeed is no data for the filters you
 * applied. For this reason, whenever filters change, this hook helps you just
 * reset the page back to 1.
 * @param pageAndTotalRecords the current page and total # of results for that page for any data fetched
 * @param newFilters any filters applied to get the data
 * @returns should the page be reset back to 1 and fetch data for that page instead?
 */
export const useShouldPageReset = (
  pageAndTotalRecords: Pick<PaginationType, "page" | "total">,
  newFilters: object = {}
) => {
  const { page: newPage, total: newTotal } = pageAndTotalRecords;
  const filters = useRef(newFilters);
  const pageDuringFilterChange = useRef<number | null>(newPage ?? null);
  const totalDuringFilterChange = useRef<number | null>(newTotal ?? null);
  const shouldReset = useRef(false);

  // if filters changed, sync the ref, flag for resetting page, and remember the page and # of results at this time
  if (!isEqual(filters.current, newFilters)) {
    filters.current = newFilters;
    pageDuringFilterChange.current = newPage;
    totalDuringFilterChange.current = newTotal ?? 0;
    shouldReset.current = true;
  } else if (
    // if a re-render happens for any reason and the page # or the total # of results has changed
    // that means new data has or is coming in, so we can turn off the resetting flag
    pageDuringFilterChange.current !== newPage ||
    totalDuringFilterChange.current !== newTotal
  ) {
    shouldReset.current = false;
    pageDuringFilterChange.current = null;
    totalDuringFilterChange.current = null;
  }

  return shouldReset.current;
};

/**
 * Keep track of sorting and pagination props, resetting the page back to 1 if filters change
 * @param initialState any combination of sorting / pagination props to use by default during first load
 * @param filters filters applied to fetching data
 * @returns [tableState, updateTableState] - pass tableState to the pagination props in the DataTable. pass updateTableState to the onPaginationChange and onSortChange props of the DataTable
 */
export const useTableSortingAndPagination = (
  initialState: Partial<TableStateType> = {},
  filters?: object,
  storageKey?: string
) => {
  const [tableState, setTableState] = useStickyState<TableStateType>(
    {
      page: 1,
      pageSize: ITEMS_PER_PAGE,
      total: 0,
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
      groupBy: [],
      ...initialState,
    },
    storageKey ?? "table-state"
  );

  const updateTableState = (update: Partial<TableStateType>) => {
    setTableState((prev) => ({
      ...prev,
      ...update,
    }));
  };

  const shouldResetPage = useShouldPageReset(tableState, filters);

  useEffect(() => {
    if (shouldResetPage) {
      updateTableState({ page: 1 });
    }
  }, [shouldResetPage]);

  return [tableState, updateTableState] as const;
};

export const useTablesSortingAndPagination = (
  tableKeys: string[],
  sharedInitialState: Partial<TableStateType> = {},
  storageKey?: string
) => {
  const [tableStates, setTableStates] = useStickyState<
    Map<string, TableStateType>
  >(
    new Map(
      tableKeys
        .filter((k) => k != undefined && k != null)
        .map((k) => [
          k,
          {
            page: 1,
            pageSize: ITEMS_PER_PAGE,
            sortBy: "created_at",
            sortDirection: SortDirection.DESCENDING,
            groupBy: [],
            ...sharedInitialState,
          },
        ])
    ),
    storageKey ?? "group-by-tables-state"
  );

  const updateTableStates = (
    state: Map<string, TableStateType>,
    action:
      | {
          type: "create" | "update";
          key: string;
          tableState: Partial<TableStateType>;
        }
      | {
          type: "delete";
          key: string;
        }
      | {
          type: "clear";
        }
  ) => {
    if (action.type === "create") {
      setTableStates((prev) =>
        produce(prev, (draft) =>
          draft.set(action.key, {
            page: 1,
            pageSize: 25,
            groupBy: [],
            ...sharedInitialState,
            sortBy: "created_at",
            sortDirection: SortDirection.DESCENDING,
            ...action.tableState,
          })
        )
      );
    }
    if (action.type === "update") {
      if (action.key && state.has(action.key)) {
        setTableStates((prev) =>
          produce(prev, (draft) =>
            draft.set(action.key, {
              page: 1,
              pageSize: 25,
              sortBy: "created_at",
              sortDirection: SortDirection.DESCENDING,
              groupBy: [],
              ...(state.get(action.key) ?? {}),
              ...action.tableState,
            })
          )
        );
      }
    } else if (action.type === "delete") {
      if (action.key && state.has(action.key)) {
        setTableStates((prev) =>
          produce(prev, (draft) => {
            draft.delete(action.key);
          })
        );
      }
    } else if (action.type === "clear") {
      setTableStates((prev) =>
        produce(prev, (draft) => {
          draft.clear();
        })
      );
    }
  };
  const previousTableKeys = usePrevious(tableKeys);

  // keep tableKeys in sync with table states at all times
  useEffect(() => {
    // Do not reset state if we already have saved the state and the previousTableKeys are empty
    // e.g. when refreshing the page or when closing and reopening the page
    if (!isEmpty(tableStates) && isEmpty(previousTableKeys)) return;

    if (!tableKeys || tableKeys.every((key) => !key)) {
      if (tableStates.size > 0) {
        updateTableStates(tableStates, { type: "clear" });
      }
      return;
    }
    if (
      isEqual(tableKeys, previousTableKeys) ||
      (!isEmpty(tableStates) && !previousTableKeys)
    )
      return;

    for (const key of tableKeys) {
      if (!key || tableStates.has(key)) continue;
      updateTableStates(tableStates, {
        type: "create",
        key,
        tableState: {},
      });
    }

    if (previousTableKeys) {
      for (const key of previousTableKeys) {
        if (!key) continue;

        if (!tableKeys.includes(key)) {
          updateTableStates(tableStates, {
            type: "delete",
            key,
          });
        }
      }
    }
  }, [tableKeys]);

  return [tableStates, updateTableStates] as const;
};

/** If the backend returns the entire collection, we can slice and sort ourselves */
export const useClientSideSortingAndPagination = <TData,>(
  data: TData[] = [],
  tableState: TableStateType
) => {
  const { data: sortedData } = useSort(data, {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  });
  const { data: paginatedData } = usePagination(sortedData, {
    page: tableState.page,
    pageSize: tableState.pageSize,
  });

  return {
    items: paginatedData,
    pagination: {
      page: tableState.page,
      pageSize: tableState.pageSize,
      total: data.length,
    },
    sorting: {
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
    },
  };
};
